import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { kcActionStatus, type KcActionState } from '@/lib/kcAction';
import { callbackDestination } from '@/lib/landing';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { getUserManager, isLoading } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (isLoading) return; // Wait for AuthContext to initialize the UserManager
    if (processed.current) return;
    processed.current = true;

    handleCallback();
  }, [isLoading]);

  async function handleCallback() {
    try {
      const um = getUserManager();
      if (!um) {
        console.error('[auth-callback] UserManager not available');
        navigate('/login', { replace: true });
        return;
      }

      // Read before the callback is processed: a redirect back from a
      // Keycloak required action the SPA asked for (#998, changePassword)
      // carries its outcome next to the code, and the page it started from
      // shows it.
      const actionStatus = kcActionStatus(window.location.search);
      const user = await um.signinCallback();
      // An explicit page to return to wins; otherwise `/` lands by role (#1155).
      const returnTo = callbackDestination((user?.state as { returnTo?: unknown } | undefined)?.returnTo);
      const state: KcActionState | undefined = actionStatus ? { kcActionStatus: actionStatus } : undefined;
      navigate(returnTo, { replace: true, state });
    } catch (err) {
      console.error('[auth-callback] Sign-in callback failed:', err);
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-surface-deep">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold mx-auto mb-4" />
        <p className="text-silver-muted text-sm">Completing sign in...</p>
      </div>
    </div>
  );
}
